'use client';

import { GetActivitiesAdminStatusFilter } from '@api/models';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';

import { AdminActivity, getActivitiesAdmin } from '../services/activities.service';

/**
 * Hook for admin activities list.
 * Uses the dedicated admin endpoint that returns ALL activities including those without slots.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useActivitiesAdmin();
 * // All activities are returned, filter client-side as needed
 * const pending = data?.filter(a => a.status === 'pending');
 * ```
 */
interface UseActivitiesAdminFilters {
  statusFilter?: GetActivitiesAdminStatusFilter;
  /**
   * Filter by effective audience (activity.scopes ∪ owner.user.groups). Empty
   * disables the filter. Include `__none__` to also match unscoped activities
   * owned by ungrouped users.
   */
  groupIds?: string[];
}

export function useActivitiesAdmin(filters: UseActivitiesAdminFilters = {}) {
  const locale = useLocale();

  const groupIdsKey = (filters.groupIds ?? []).slice().sort().join('|');
  return useQuery<AdminActivity[]>({
    queryKey: ['activities-admin', locale, filters.statusFilter, groupIdsKey],
    queryFn: async () => {
      const result = await getActivitiesAdmin({
        locale,
        statusFilter: filters.statusFilter,
        groupIds: filters.groupIds && filters.groupIds.length > 0 ? filters.groupIds : undefined
      });
      return result.activities;
    },
    staleTime: 60 * 1000 // 1 minute
  });
}
